import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button, Box, Paper, Grid } from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import { CSVLink } from "react-csv";
import { CustomPagination } from "../../Components/CustomPagination";
import { CustomLoader } from "../../Components/CustomLoader";
import ProductForecastService from "../../services/ProductForecastService";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { Popup } from "../../Components/Popup";
import { AnticipatedDateUpdate } from "./AnticipatedDateUpdate";
import CustomAutocomplete from "../../Components/CustomAutocomplete";
import SearchComponent from "../../Components/SearchComponent ";
import { MessageAlert } from "../../Components/MessageAlert";
import { useNotificationHandling } from "../../Components/useNotificationHandling ";
import CustomTextField from "../../Components/CustomTextField";

export const CurrentMonthForecastView = () => {
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [salesPersonByFilter, setSalesPersonByFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentMonthForecast, setCurrentMonthForecast] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [idForEdit, setIdForEdit] = useState("");
  const csvLinkRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const UserData = useSelector((state) => state.auth.profile);
  const assignedOption = UserData.sales_customer_user_forecast || [];
  console.log(assignedOption);
  const [startDate, setStartDate] = useState("");
  const { handleSuccess, handleError, handleCloseSnackbar, alertInfo } =
    useNotificationHandling();

  useEffect(() => {
    const beforePrint = () => {
      setIsPrinting(true);
      setCurrentMonthForecast([]);
    };

    const afterPrint = () => {
      setIsPrinting(false);
      // Fetch the data again and update the companyData state
      getAllCurrentMonthForecastDetails();
    };

    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);

    return () => {
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, []);

  const headers = [
    { label: "Company", key: "company" },
    { label: "Sales Person", key: "assign_to_email" },
    { label: "Product", key: "product" },
    { label: "Forecast", key: "forecast" },
    { label: "Actual", key: "actual" },
    { label: "Orderbook Value", key: "orderbook_value" },
    { label: "Shortfall", key: "forecast_achieved" },
  ];

  const isSupplyChain = UserData.groups.includes(
    "Operations & Supply Chain Manager",
  );
  const emails = [
    "admin@glutape.com",
    "rajeev@glutape.com",
    "gaurav@glutape.com",
    "arjun@glutape.com",
    "pruthvi@glutape.com",
    "anuradha@glutape.com",
    "vivek_production@glutape.com",
    "vivek2@glutape.com",
    "managerwithoutlead@glutape.com",
    "biraj@glutape.com",
    "rushilsalian13@glutape.com",
    "it1@glutape.com",
  ];

  const handleDownload = async () => {
    try {
      const data = await handleExport();
      setExportData(data);
      setTimeout(() => {
        csvLinkRef.current.link.click();
      });
      handleSuccess("CSV Download successfully");
    } catch (error) {
      handleError(error);
      console.log("CSVLink Download error", error);
    }
  };

  const handleExport = async () => {
    try {
      setOpen(true);
      const response = await ProductForecastService.getAllCurrentMonthData(
        "all",
        salesPersonByFilter,
        searchQuery,
        startDate,
      );
      const data = response.data
        .filter((row) => row.forecast >= 0)
        .map((row) => {
          const sumValue = row.orderbook_value + row.actual;
          const forecast_achieved = row.forecast - sumValue;
          return {
            company: row.company,
            assign_to_email: row.assign_to_email.join[","],
            product: row.product,
            forecast: row.forecast,
            actual: row.actual,
            orderbook_value: row.orderbook_value,
            forecast_achieved: forecast_achieved > 0 ? forecast_achieved : 0,
          };
        });
      console.log("data", data);
      return data;
    } catch (error) {
      console.log(error);
    } finally {
      setOpen(false);
    }
  };

  const getAllCurrentMonthForecastDetails = useCallback(async () => {
    try {
      setOpen(true);
      const response = await ProductForecastService.getAllCurrentMonthData(
        currentPage,
        salesPersonByFilter,
        searchQuery,
        startDate,
      );
      setCurrentMonthForecast(response.data.results);
      const total = response.data.count;
      setTotalPages(Math.ceil(total / 25));
    } catch (error) {
      handleError(error);
      console.error("Error fetching Customer Having Forecast", error);
    } finally {
      setOpen(false);
    }
  }, [currentPage, salesPersonByFilter, searchQuery, startDate]);

  useEffect(() => {
    getAllCurrentMonthForecastDetails();
  }, [currentPage, salesPersonByFilter, searchQuery, startDate]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page with new search
  };

  const handleReset = () => {
    setSearchQuery("");
    setCurrentPage(1); // Reset to first page with no search query
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleEditClick = useCallback(
    (item) => {
      setIdForEdit(item);
      setOpenPopup(true);
    },
    [currentMonthForecast],
  );

  const handleFilterChange = (value) => {
    setSalesPersonByFilter(value);
    // getAllCurrentMonthForecastDetails(currentPage, value, searchQuery);
  };

  const formatDate = (dateString) => {
    return dateString
      ? new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(new Date(dateString))
      : "-";
  };

  const getColorForDate = (anticipatedDate) => {
    // If anticipatedDate is null, return an empty string or a default color
    if (!anticipatedDate) {
      console.log("No anticipated date provided.");
      return ""; // No color
    }

    const today = new Date();
    const anticipated = new Date(anticipatedDate);

    // You can also check if anticipated is an Invalid Date
    if (isNaN(anticipated.getTime())) {
      console.log("Invalid date provided.");
      return ""; // No color
    }

    today.setHours(0, 0, 0, 0);
    anticipated.setHours(0, 0, 0, 0);

    console.log(
      `Today: ${today.toISOString()}, Anticipated: ${anticipated.toISOString()}`,
    );

    if (anticipated.getTime() === today.getTime()) {
      // Date is today
      console.log("Date is today.");
      return "#ccccff";
    } else if (anticipated < today) {
      // Date is in the past
      console.log("Date is in the past.");
      return "#ffcccc";
    } else {
      // Date is in the future
      console.log("Date is in the future.");
      return "#ffffcc";
    }
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  // Example use:
  console.log(getColorForDate("2023-12-25")); // Future date example

  const Tableheaders = [
    "Company",
    "Sales Person",
    "Product",
    "Forecast",
    "Actual",
    "Orderbook Value",
    "Shortfall",
    "Estimate Date",
    "Action",
  ];

  return (
    <div>
      <Helmet>
        <style>
          {`
            @media print {
              html, body {
                filter: ${isPrinting ? "blur(10px)" : "none"} !important;
              }
            }
          `}
        </style>
      </Helmet>
      <MessageAlert
        open={alertInfo.open}
        onClose={handleCloseSnackbar}
        severity={alertInfo.severity}
        message={alertInfo.message}
      />
      <CustomLoader open={open} />
      <Grid item xs={12}>
        <Paper sx={{ p: 2, m: 4, display: "flex", flexDirection: "column" }}>
          <Box sx={{ marginBottom: 2, display: "flex", alignItems: "center" }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              // sx={{ marginRight: 5, marginLeft: 5 }}
            >
              {!UserData.groups.includes("Sales Executive") && (
                <Grid item xs={12} sm={3}>
                  <CustomAutocomplete
                    fullWidth
                    size="small"
                    sx={{ minWidth: 150 }}
                    onChange={(event, value) => handleFilterChange(value)}
                    value={salesPersonByFilter}
                    options={
                      isSupplyChain
                        ? emails
                        : assignedOption.length > 0 &&
                          assignedOption
                            .filter(
                              (option) =>
                                option.groups.includes("Sales Manager") || // option.groups === "Sales Manager" ||
                                option.groups.includes("Sales Executive") ||
                                option.groups.includes(
                                  "Sales Deputy Manager",
                                ) ||
                                option.groups.includes(
                                  "Sales Assistant Deputy Manager",
                                ) ||
                                option.groups.includes(
                                  "Sales Manager(Retailer)",
                                ) ||
                                option.groups.includes("Customer Service") || // option.groups === "Customer Service" ||
                                option.groups.includes("Director") ||
                                option.groups.includes(
                                  "Customer Relationship Executive",
                                ),
                            )
                            .map((option) => option.email)
                    }
                    getOptionLabel={(option) => option}
                    label="Filter By Sales Person"
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={3}>
                <CustomTextField
                  sx={{ width: "300px" }}
                  // label="Estimate date"
                  variant="outlined"
                  size="small"
                  type="date"
                  id="start-date"
                  value={startDate}
                  onChange={handleStartDateChange}
                />
              </Grid>
              {/* <Grid item xs={12} sm={3}>
                <SearchComponent
                  onSearch={handleSearch}
                  onReset={handleReset}
                />
              </Grid> */}
              <Grid item xs={12} sm={4}></Grid>
              <Grid
                item
                xs={12}
                sm={2}
                sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDownload}
                >
                  Download CSV
                </Button>
                {exportData.length > 0 && (
                  <CSVLink
                    data={exportData}
                    headers={headers}
                    ref={csvLinkRef}
                    filename={"Current Month forecast.csv"}
                    style={{ display: "none" }} // Hide the link
                  />
                )}
              </Grid>
              {/* <Grid item xs={12} sm={3}>
                <SearchComponent
                  onSearch={handleSearch}
                  onReset={handleReset}
                />
              </Grid> */}
            </Grid>
          </Box>
          <Box
            display="flex"
            gap={18}
            justifyContent="start"
            marginBottom="10px"
          >
            <SearchComponent
              width={"300px"}
              onSearch={handleSearch}
              onReset={handleReset}
            />
            <h3
              style={{
                marginBottom: "0.5em",
                fontSize: "24px",
                color: "rgb(34, 34, 34)",
                fontWeight: 800,
                textAlign: "center",
              }}
            >
              Current Month Forecast
            </h3>
          </Box>
          <TableContainer
            sx={{
              maxHeight: 440,
              "&::-webkit-scrollbar": {
                width: 15,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f2f2f2",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#aaa9ac",
              },
            }}
          >
            <Table
              sx={{ minWidth: 1200 }}
              stickyHeader
              aria-label="sticky table"
            >
              <TableHead>
                <StyledTableRow>
                  {Tableheaders.map((header) => (
                    <StyledTableCell key={header} align="center">
                      {header}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {currentMonthForecast.map((row, i) => {
                  const sumValue =
                    Number(row.orderbook_value) + Number(row.actual);

                  const forecast_achieved = (
                    Number(row.forecast) - sumValue
                  ).toFixed(2);

                  const rowColor = getColorForDate(row.anticipated_date);

                  // Get the color for the date

                  console.log(sumValue, forecast_achieved, rowColor);
                  return (
                    <StyledTableRow
                      key={row.id}
                      sx={{
                        backgroundColor: rowColor,
                      }}
                    >
                      <StyledTableCell align="center">
                        {row.company}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.assign_to_email.join(", ")}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.product}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.forecast && Number(row.forecast).toFixed(2)}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.actual && Number(row.actual).toFixed(2)}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.orderbook_value &&
                          Number(row.orderbook_value).toFixed(2)}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {forecast_achieved > 0 ? forecast_achieved : 0}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {formatDate(row.anticipated_date)}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Button onClick={() => handleEditClick(row)}>
                          View
                        </Button>
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination
            totalPages={totalPages}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
          />
        </Paper>
      </Grid>
      <Popup
        maxWidth="md"
        title={"Update Current Month Forecast"}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
      >
        <AnticipatedDateUpdate
          idForEdit={idForEdit}
          setOpenPopup={setOpenPopup}
          getAllCurrentMonthForecastDetails={getAllCurrentMonthForecastDetails}
        />
      </Popup>
    </div>
  );
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    padding: 0, // Remove padding from header cells
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: 0, // Remove padding from body cells
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
